/**
 * # Artifact Service Client
 *
 * Client for submitting artifacts to the anode artifact service.
 * Handles PNG image processing and submission to the existing artifact backend.
 */

import type {
  ArtifactSubmissionOptions,
  ArtifactSubmissionResult,
  IArtifactClient,
} from "./types.ts";
import { uploadArtifactViaProjects } from "./projects-artifacts.ts";

export function decodeImageData(base64String: string): Uint8Array<ArrayBuffer> {
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Client for interacting with the anode artifact service
 */
export class ArtifactClient implements IArtifactClient {
  baseUrl: string;
  private useProjectsArtifacts: boolean;

  // TODO: Make artifact service URL configuration more general for @runt/lib package
  constructor(
    baseUrl: string = "https://app.runt.run",
    options?: { useProjectsArtifacts?: boolean }
  ) {
    this.baseUrl = baseUrl;
    this.useProjectsArtifacts = options?.useProjectsArtifacts ?? false;
  }

  /**
   * Submit content data to artifact service
   */
  async submitContent(
    data: Uint8Array<ArrayBuffer>,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    // For now, delegate to PNG-specific implementation
    // TODO: Generalize to handle any content type
    return await this.submitPng(data, options);
  }

  /**
   * Submit PNG image data to the artifact service
   */
  async submitPng(
    pngData: Uint8Array<ArrayBuffer>,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    if (!this.isPngData(pngData)) {
      throw new Error("Invalid PNG data: missing PNG header");
    }

    return await this.submitArtifact(pngData, {
      ...options,
      mimeType: "image/png",
    });
  }

  /**
   * Submit PNG from base64 string
   */
  async submitPngFromBase64(
    base64Data: string,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    try {
      const pngData = decodeImageData(base64Data);
      return await this.submitPng(pngData, options);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to decode base64 PNG data: ${message}`);
    }
  }

  /**
   * Legacy single-step artifact submission (R2-backed)
   */
  private async submitArtifactLegacy(
    data: Uint8Array,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    const url = `${this.baseUrl}/api/artifacts`;

    const headers: Record<string, string> = {
      authorization: `Bearer ${options.authToken}`,
      "x-notebook-id": options.notebookId,
    };

    if (options.mimeType) {
      headers["content-type"] = options.mimeType;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: new Uint8Array(data).buffer,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: "Unknown error",
        }));
        throw new Error(
          `Artifact submission failed: ${error.error || response.statusText}`
        );
      }

      const result = await response.json();
      return result as ArtifactSubmissionResult;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to submit artifact: ${String(error)}`);
    }
  }

  /**
   * Projects-backed three-step artifact submission
   */
  private async submitArtifactViaProjects(
    data: Uint8Array,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    const result = await uploadArtifactViaProjects({
      baseUrl: this.baseUrl,
      notebookId: options.notebookId,
      authToken: options.authToken,
      filename: options.filename || "artifact.bin",
      mimeType: options.mimeType || "application/octet-stream",
      body: new Uint8Array(data).buffer,
    });

    return {
      artifactId: result.artifactId,
      fileUrl: result.fileUrl,
    };
  }

  /**
   * Submit arbitrary artifact data
   */
  async submitArtifact(
    data: Uint8Array,
    options: ArtifactSubmissionOptions
  ): Promise<ArtifactSubmissionResult> {
    if (this.useProjectsArtifacts) {
      return await this.submitArtifactViaProjects(data, options);
    } else {
      return await this.submitArtifactLegacy(data, options);
    }
  }

  /**
   * Retrieve artifact by ID
   */
  async retrieveArtifact(artifactId: string): Promise<Uint8Array> {
    const url = `${this.baseUrl}/api/artifacts/${artifactId}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Artifact not found: ${artifactId}`);
        }
        throw new Error(`Failed to retrieve artifact: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to retrieve artifact: ${String(error)}`);
    }
  }

  /**
   * Get the public URL for an artifact (for direct access)
   * If useProjectsArtifacts is enabled and fileUrl is in container metadata, use it.
   * Otherwise, fall back to the legacy API endpoint.
   */
  getArtifactUrl(container: {
    artifactId: string;
    metadata?: Record<string, unknown>;
  }): string {
    // If Projects artifacts are enabled, check for fileUrl in metadata
    if (this.useProjectsArtifacts) {
      const fileUrl =
        typeof container.metadata?.fileUrl === "string"
          ? container.metadata.fileUrl
          : undefined;
      if (fileUrl) {
        return fileUrl;
      }
    }
    // Otherwise, use the legacy API endpoint
    return `${this.baseUrl}/api/artifacts/${container.artifactId}`;
  }

  /**
   * Validate PNG data by checking for PNG header
   */
  private isPngData(data: Uint8Array): boolean {
    if (data.length < 8) return false;

    // PNG signature: 89 50 4E 47 0D 0A 1A 0A
    const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

    for (let i = 0; i < 8; i++) {
      if (data[i] !== pngSignature[i]) {
        return false;
      }
    }

    return true;
  }
}

/**
 * Create an artifact client with default configuration
 */
export function createArtifactClient(baseUrl?: string): ArtifactClient {
  return new ArtifactClient(baseUrl);
}

/**
 * Process and submit PNG images to the artifact service
 */
export class PngProcessor {
  client: ArtifactClient;
  constructor(client: ArtifactClient) {
    this.client = client;
  }

  /**
   * Validate PNG dimensions and file size
   */
  validatePng(data: Uint8Array, maxSizeBytes: number = 10 * 1024 * 1024): void {
    if (!this.client["isPngData"](data)) {
      throw new Error("Invalid PNG data");
    }

    if (data.length > maxSizeBytes) {
      throw new Error(
        `PNG file too large: ${data.length} bytes (max: ${maxSizeBytes})`
      );
    }

    // Could add more validation here:
    // - Image dimensions
    // - Color depth
    // - Compression ratio
  }
}
